import React, { useState } from "react";
import { FaEnvelope, FaLock, FaUserShield } from "react-icons/fa";
import Illustration from "../Assets/forget.png";

const ForgetPassword = () => {
  const [mode, setMode] = useState("reset"); // "reset" or "change"
  const [adminID, setAdminID] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newAdminID, setNewAdminID] = useState("");
  const [message, setMessage] = useState({ content: "", type: "" });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "adminID") setAdminID(value);
    else if (name === "newPassword") setNewPassword(value);
    else if (name === "confirmPassword") setConfirmPassword(value);
    else if (name === "newAdminID") setNewAdminID(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ content: "", type: "" });

    // Validation
    if (!adminID) {
      setMessage({ content: "Admin ID is required.", type: "danger" });
      return;
    }

    // Check if admin ID is allowed
    const allowedEmails = ['contact@bookscomply.com', 'contact@nareshco.com'];
    if (!allowedEmails.includes(adminID)) {
      setMessage({ 
        content: "This feature is only available for authorized admin accounts.", 
        type: "danger" 
      });
      return;
    }

    if (mode === "reset") {
      if (!newPassword || !confirmPassword) {
        setMessage({ content: "All fields are required.", type: "danger" });
        return;
      }
      if (newPassword !== confirmPassword) {
        setMessage({ content: "Passwords do not match.", type: "danger" });
        return;
      }
      if (newPassword.length < 6) {
        setMessage({ content: "Password must be at least 6 characters.", type: "danger" });
        return;
      }
    } else if (mode === "change") {
      if (!newAdminID) {
        setMessage({ content: "New Admin ID is required.", type: "danger" });
        return;
      }
      if (adminID === newAdminID) {
        setMessage({ content: "New Admin ID must be different from current.", type: "danger" });
        return;
      }
    }

    setLoading(true);

    try {
      const endpoint = mode === "reset" 
        ? `${process.env.REACT_APP_API_BASE_URL}/api/admin/reset-password`
        : `${process.env.REACT_APP_API_BASE_URL}/api/admin/change-username`;
      
      const body = mode === "reset"
        ? { adminID, newPassword }
        : { currentAdminID: adminID, newAdminID };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Operation failed.");
      }

      setMessage({
        content: mode === "reset" 
          ? "Password reset successfully! You can now login with your new password."
          : `Admin ID changed successfully! Your new Admin ID is: ${data.newAdminID}`,
        type: "success",
      });
      
      // Clear form
      setAdminID("");
      setNewPassword("");
      setConfirmPassword("");
      setNewAdminID("");
    } catch (error) {
      setMessage({ content: error.message, type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-dark-900 dark:to-dark-800 p-4">
      <div className="w-full max-w-4xl bg-white dark:bg-dark-800 rounded-2xl shadow-xl overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Left Side Illustration */}
          <div className="hidden md:flex items-center justify-center p-8 bg-primary-50 dark:bg-dark-700">
            <img src={Illustration} alt="Forget Password" className="max-w-full h-auto" />
          </div>

          {/* Right Side Form */}
          <div className="p-8">
            <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-4">
              {mode === "reset" ? "Reset Password" : "Change Admin ID"}
            </h2>
            <p className="text-center text-gray-500 dark:text-gray-400 mb-6">
              {mode === "reset" 
                ? "Only available for authorized admin accounts (contact@bookscomply.com, contact@nareshco.com)"
                : "Change your Admin ID (username)"}
            </p>

            {/* Mode Toggle */}
            <div className="flex gap-2 mb-6">
              <button
                type="button"
                onClick={() => {
                  setMode("reset");
                  setMessage({ content: "", type: "" });
                }}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  mode === "reset"
                    ? "bg-primary-600 text-white"
                    : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600"
                }`}
              >
                Reset Password
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode("change");
                  setMessage({ content: "", type: "" });
                }}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  mode === "change"
                    ? "bg-primary-600 text-white"
                    : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600"
                }`}
              >
                Change Admin ID
              </button>
            </div>

            {message.content && (
              <div className={`p-4 rounded-xl mb-4 ${message.type === "success" ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400" : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400"}`} role="alert">
                {message.content}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Admin ID Input (always shown) */}
              <div className="relative mb-4">
                <FaUserShield className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="adminID"
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Current Admin ID"
                  value={adminID}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* Password Reset Fields */}
              {mode === "reset" && (
                <>
                  <div className="relative mb-4">
                    <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="password"
                      name="newPassword"
                      className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="New Password"
                      value={newPassword}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="relative mb-4">
                    <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="password"
                      name="confirmPassword"
                      className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Confirm New Password"
                      value={confirmPassword}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </>
              )}

              {/* Username Change Field */}
              {mode === "change" && (
                <div className="relative mb-4">
                  <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="newAdminID"
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="New Admin ID"
                    value={newAdminID}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              )}

              <button
                className="w-full py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                type="submit"
                disabled={loading}
              >
                {loading 
                  ? (mode === "reset" ? "Resetting Password..." : "Changing Admin ID...") 
                  : (mode === "reset" ? "Reset Password" : "Change Admin ID")}
              </button>
            </form>

            <div className="text-center mt-4">
              <a href="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                Back to Login
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgetPassword;
